/**
 * Lazy-load wrapper that survives chunk-hash mismatches after a deploy.
 *
 * Problem: when a new build is deployed while a user is mid-session, their
 * already-loaded SPA holds references to chunk hashes that no longer exist
 * on the server. The next React.lazy() import fails with "Failed to fetch
 * dynamically imported module" or similar, and the page renders the error
 * boundary ("Lazy" component error).
 *
 * Fix:
 *  1. Retry the import a couple of times in case it's a transient blip.
 *  2. If retries fail, force a one-time hard reload (guarded by a
 *     sessionStorage flag so we don't get into a refresh loop).
 *
 * Usage: `const Page = lazyWithReload(() => import("./pages/Foo"))` instead
 * of `lazy(() => import("./pages/Foo"))`.
 */
import { lazy, type ComponentType } from "react";

const RELOAD_FLAG = "tap-lazy-reload-attempted";

function isChunkLoadError(err: unknown): boolean {
  if (!err) return false;
  const msg = String((err as any)?.message || err).toLowerCase();
  return (
    msg.includes("failed to fetch dynamically imported module") ||
    msg.includes("loading chunk") ||
    msg.includes("loading css chunk") ||
    msg.includes("import error") ||
    msg.includes("module specifier") ||
    msg.includes("error loading dynamically imported") ||
    msg.includes("dynamic import") ||
    msg.includes("dynamically imported module") ||
    (err as any)?.name === "ChunkLoadError"
  );
}

async function withRetries<T>(fn: () => Promise<T>, attempts = 2, delayMs = 350): Promise<T> {
  let lastErr: unknown;
  for (let i = 0; i <= attempts; i++) {
    try {
      return await fn();
    } catch (err) {
      lastErr = err;
      if (i < attempts) {
        await new Promise((r) => setTimeout(r, delayMs * (i + 1)));
      }
    }
  }
  throw lastErr;
}

export function lazyWithReload<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
): React.LazyExoticComponent<T> {
  return lazy(async () => {
    try {
      return await withRetries(factory);
    } catch (err) {
      if (isChunkLoadError(err)) {
        // DEV mode: do NOT auto-reload. Vite HMR rewrites chunks
        // constantly during development, which causes transient
        // dynamic-import failures. Reloading the page on every one
        // of those was wiping the user's session mid-edit, every
        // 30-60 seconds. In dev we just rethrow and let React's
        // error boundary show — a manual refresh is one click and
        // far less disruptive than the silent surprise reload.
        const isDev = typeof import.meta !== "undefined" && (import.meta as any).env?.DEV;
        if (isDev) {
          console.warn("[lazyWithReload] chunk load error in dev — NOT auto-reloading", err);
          throw err;
        }
        try {
          if (typeof window !== "undefined") {
            const already = window.sessionStorage.getItem(RELOAD_FLAG);
            if (!already) {
              window.sessionStorage.setItem(RELOAD_FLAG, String(Date.now()));
              // Force-reload from server so the new index.html + chunk hashes
              // are picked up. Once that load succeeds, the next page nav will
              // clear the flag.
              window.location.reload();
              // Return a never-resolving promise so React doesn't try to
              // render anything before the reload kicks in.
              await new Promise(() => {});
            }
          }
        } catch {
          // ignore — fall through and rethrow
        }
      }
      throw err;
    }
  });
}

// Clear the reload-attempted flag once the app has successfully booted past
// initial render. Called from main.tsx / AppProvider on first mount.
export function clearLazyReloadFlag(): void {
  try {
    if (typeof window !== "undefined") {
      window.sessionStorage.removeItem(RELOAD_FLAG);
    }
  } catch {
    // no-op
  }
}
